import styles from "./PortfolioCompanyProfilePage.module.scss";
import { Loader } from "@/frontend/shared/ui/Loader/Loader";
import { HeaderSection } from "./HeaderSection/HeaderSection";
import { ESectionOptions, Sidebar } from "@/frontend/widgets/Sidebar";
import { useEffect, useMemo, useRef, useState } from "react";
import { FinancingSection } from "@/frontend/features/Company/Profile/ui/FinancingSection/FinancingSection";
import { useScrollToTop } from "@/frontend/shared/lib/hooks/useScrollToTop";
import { addSpacesToCamelCase } from "@/frontend/shared/lib/addSpacesToCamelCase/addSpacesToCamelCase";
import { DataRoomSection } from "./DataRoomSection/ui/DataRoomSection";
import { getRouteDashboard } from "@/frontend/shared/const/router";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { useHistoricalNavigate } from "@/frontend/shared/lib/useHistoricalNavigate/useHistoricalNavigate";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { convertStringWithAndSymbol } from "@/frontend/shared/lib/convertStringWithAndSymbol/convertStringWithAndSymbol";
import { InnoveraCompanyHeader } from "@/frontend/components/InnoveraCompanyHeader";
import useHeight from "@/frontend/shared/lib/hooks/useHeight";
import useScrollProgress from "@/frontend/shared/lib/hooks/useScrollProgress";
import { setFileProgressesAtom } from "@/frontend/atoms/data-room/upload";
import { AxiosError } from "axios";
import { useSearchParams } from "react-router-dom";
import { AnnotationsSection } from "@/frontend/pages/CompanyProfilePage/ui/AnnotationsSection/ui/AnnotationsSection";
import { InvestorReportAnErrorModal } from "@/frontend/components/InvestorReportAnErrorModal/ui/InvestorReportAnErrorModal";
import { Comments } from "@/frontend/widgets/CommentsSection";
import { useScreenWidth } from "@/frontend/shared/lib/hooks/useScreenWidth";
import clsx from "clsx";
import { CommentsDrawer } from "@/frontend/widgets/CommentsSection/ui/CommentsDrawer/CommentsDrawer";
import { COMMENTS_BREAKPOINT } from "@/frontend/shared/const/breakpoints";
import { MarketLandscapeSection } from "@/frontend/features/Company/Profile/ui/MarketLandscapeSection/MarketLandscapeSection";
import { MarketOverviewSection } from "@/frontend/features/Company/Profile/ui/MarketOverviewSection/MarketOverviewSection";
import { SolutionOverviewSection } from "@/frontend/features/Company/Profile/ui/SolutionOverviewSection/SolutionOverviewSection";
import { getDisplayingDataAccess } from "@/frontend/shared/lib/getDisplayingDataAccess/getDisplayingDataAccess";
import { EActiveView, EDataStatus, EInvestorAccess, TFounderInfo } from "@/frontend/features/Company/model/types/types";
import { TeamSection } from "@/frontend/features/Company/Profile/ui/TeamSection/TeamSection";
import { OverviewSection } from "@/frontend/features/Company/Profile/ui/OverviewSection/OverviewSection";
import { getFounderInfo } from "@/frontend/features/Company/model/getFounderInfo";
import { postAccessControlsRequest } from "@/frontend/features/Company/model/postAccessControlsRequest";
import { getPortfolioCompanyData } from "../lib/getPortfolioCompanyData";
import { getCompanyIdFromUrl } from "@/frontend/shared/lib/getCompanyIdFromUrl/getCompanyIdFromUrl";
import { postPortfolioCompanySectionNote } from "@/frontend/features/Company/model/postPortfolioCompanySectionNote";
import { deletePortfolioCompanySectionNote } from "@/frontend/features/Company/model/deletePortfolioCompanySectionNote";
import { patchPortfolioCompanySectionNote } from "@/frontend/features/Company/model/patchPortfolioCompanySectionNote";
import { getPortfolioCompanyListNotes } from "@/frontend/features/Company/model/getPortfolioCompanyListNotes";
import { getCompanyShortInfo } from "../lib/getCompanyShortInfo";

export function PortfolioCompanyProfilePage() {
  const [companyId, setCompanyId] = useState<number>(getCompanyIdFromUrl({ url: window.location.href }));
  const [isLoadingRequestAccess, setIsLoadingRequestAccess] = useState(false);

  const {
    detailsData,
    isLoadingDetailsData,
    isFetchingDetailsData,
    refetchDetailsData,
    detailsDataStatus,
    competitorCompaniesData,
    isLoadingCompetitorCompaniesData,
    refetchCompetitorCompaniesData,
    competitorsDataStatus,
    similarCompaniesData,
    isLoadingSimilarCompaniesData,
    refetchSimilarCompaniesData,
    similarCompaniesDataStatus,
    marketOverviewData,
    isLoadingMarketOverviewData,
    refetchMarketOverviewData,
    marketOverviewDataStatus,
    patentsData,
    isLoadingPatentsData,
    refetchPatentsData,
    productsData,
    isLoadingProductsData,
    refetchProductsData,
    productsDataStatus,
    teamData,
    isLoadingTeamData,
    refetchTeamData,
    listNotesData,
    refetchListNotes,
    founderInfoData,
    refetchFounderInfo
  } = getPortfolioCompanyData(companyId, setIsLoadingRequestAccess);

  const hasPitchdeckData = detailsData?.hasPitchdeckData;
  const hasInnoveraData = detailsData?.hasInnoveraData;
  const logoUrl = detailsData?.companyData?.logoUrl;
  const companyName = detailsData?.name ?? detailsData?.companyData?.name;

  useScrollToTop();

  const [searchParams, setSearchParams] = useSearchParams();
  const initialActiveView = searchParams.get("view");
  const [activeView, setActiveView] = useState<EActiveView>((initialActiveView as EActiveView) || EActiveView.PROFILE);
  const initialSelectedSection =
    activeView === EActiveView.PROFILE && hasInnoveraData && hasPitchdeckData
      ? ESectionOptions.TEAM
      : ESectionOptions.OVERVIEW;
  const [selectedSection, setSelectedSection] = useState<ESectionOptions>(initialSelectedSection);
  const [isCommentsSectionOpen, setIsCommentsSectionOpen] = useState(false);
  const { navigate } = useHistoricalNavigate();

  const { ref: sidebarRef, height: sidebarHeightFromRef } = useHeight();
  const { ref: headerRef, height: headerHeight } = useHeight();

  const scrollProgress = useScrollProgress();
  const [isContainerScrolled, setIsContainerScrolled] = useState(true);
  const isMobile = useScreenWidth() <= 768;
  const showComments = useScreenWidth() >= COMMENTS_BREAKPOINT;
  useEffect(() => {
    if (scrollProgress > 15 && isContainerScrolled) {
      setIsContainerScrolled(false);
    } else if (scrollProgress < 10 && !isContainerScrolled) {
      setIsContainerScrolled(true);
    }
  }, [scrollProgress, isContainerScrolled]);

  const setFileProgresses = setFileProgressesAtom();
  useEffect(() => {
    // Let's remove all file upload progress client-side data
    // on unmount - J
    return () => setFileProgresses({});
  }, [setFileProgresses]);

  const sectionRefs = {
    [ESectionOptions.OVERVIEW]: useRef(null),
    [ESectionOptions.FINANCING]: useRef(null),
    [ESectionOptions.TEAM]: useRef(null),
    [ESectionOptions.MARKET]: useRef(null),
    [ESectionOptions.PRODUCTS_AND_SERVICES]: useRef(null),
    [ESectionOptions.COMPETITIVE_LANDSCAPE]: useRef(null)
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        let mostVisibleSection = null;
        let maxRatio = 0;

        entries.forEach((entry) => {
          if (entry.intersectionRatio > maxRatio + 0.1) {
            // 0.1 is a buffer to prevent rapid switching - A.
            maxRatio = entry.intersectionRatio;
            mostVisibleSection = entry.target.getAttribute("data-section");
          }
        });

        if (mostVisibleSection) {
          setSelectedSection(mostVisibleSection);
        }
      },
      {
        rootMargin: `-${headerHeight}px 0px 0px 0px`,
        threshold: 0.01
      }
    );

    Object.values(sectionRefs).forEach((ref) => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => {
      Object.values(sectionRefs).forEach((ref) => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, [sectionRefs, setSelectedSection, headerHeight]);

  const dataStatusFromAccess = getDisplayingDataAccess(founderInfoData || ({} as TFounderInfo));

  const [noteValue, setNoteValue] = useState("");

  async function onClickPost() {
    addSectionNote({ content: noteValue });
    setNoteValue("");
  }

  const [isReportErrorModalOpen, setIsReportErrorModalOpen] = useState(false);

  const onReportError = () => setIsReportErrorModalOpen(true);

  const notesData = {
    summarySection: listNotesData?.filter((note) => note.section === ESectionOptions.OVERVIEW),
    financingSection: listNotesData?.filter((note) => note.section === ESectionOptions.FINANCING),
    teamSection: listNotesData?.filter((note) => note.section === ESectionOptions.TEAM),
    marketSection: listNotesData?.filter((note) => note.section === ESectionOptions.MARKET),
    solutionOverviewSection: listNotesData?.filter(
      (note) => note.section === convertStringWithAndSymbol(ESectionOptions.PRODUCTS_AND_SERVICES)
    ),
    marketLandscapeSection: listNotesData?.filter(
      (note) => addSpacesToCamelCase(note.section) === ESectionOptions.COMPETITIVE_LANDSCAPE
    )
  };

  async function addSectionNote({ content }: { content: string }) {
    await postPortfolioCompanySectionNote({ content, id: companyId });
    refetchListNotes();
  }

  const combinedTags = useMemo(() => {
    return detailsData?.tags;
  }, [detailsData]);

  async function onConfirmRequestDataAccess() {
    try {
      setIsLoadingRequestAccess(true);
      await postAccessControlsRequest({
        companyId: detailsData?.companyData?.id || 0,
        access: EInvestorAccess.FULL
      });
      snackbarConstructor("Access request sent", "normal");
      refetchFounderInfo();
    } catch (error) {
      snackbarConstructor("Request data access error", "error");
      reportErrorToSentry(error as AxiosError, "PORTFOLIO COMPANY PROFILE PAGE: /access-controls/request");
    }
  }

  function onChangeActiveView(view: EActiveView) {
    setActiveView(view);
    setSearchParams({ view }, { replace: true });
  }

  const headerData = getCompanyShortInfo(detailsData);
  const styleMarginTop = isMobile
    ? {}
    : {
        marginTop: `-${sidebarHeightFromRef}px`
      };

  const sectionStyles = !showComments ? styles.mobileSectionContainer : styles.sectionContainer;

  const content = useMemo(() => {
    switch (true) {
      case isLoadingDetailsData:
        return (
          <div className={styles.container}>
            <InnoveraCompanyHeader />
            <div className={styles.loadingContentContainer}>
              <Loader />
            </div>
          </div>
        );
      case activeView === EActiveView.DATA_ROOM:
        return (
          <div className={styles.container}>
            <InnoveraCompanyHeader />
            <div className={styles.contentContainer}>
              <HeaderSection
                pipeline={detailsData?.stage?.pipeline}
                setIsCommentsSectionOpen={setIsCommentsSectionOpen}
                data={headerData}
                activeView={activeView}
                onChangeActiveView={onChangeActiveView}
                companyTags={combinedTags}
                companyId={companyId}
                stageName={detailsData?.stage?.name || ""}
                triggerRefetch={refetchDetailsData}
                refetchDetailsWithPolledData={refetchDetailsData}
                dataStatusFromAccess={dataStatusFromAccess}
                triggerRefetchAccess={refetchFounderInfo}
                isLoadingRequestAccess={isLoadingRequestAccess}
                setIsLoadingRequestAccess={setIsLoadingRequestAccess}
                isDataRoomPending={founderInfoData?.isDataRoomPending}
                isContainerScrolled={isContainerScrolled}
              />
              <DataRoomSection
                id={companyId}
                dataStatusFromAccess={dataStatusFromAccess}
                companyDataId={detailsData?.companyData?.id}
                triggerRefetchAccess={refetchFounderInfo}
                isLoadingRequestAccess={isLoadingRequestAccess}
                setIsLoadingRequestAccess={setIsLoadingRequestAccess}
                isDataRoomPending={founderInfoData?.isDataRoomPending}
              />
            </div>
          </div>
        );
      case activeView === EActiveView.ANNOTATIONS:
        return (
          <div className={styles.container}>
            <InnoveraCompanyHeader />
            <div className={styles.contentContainer}>
              <HeaderSection
                setIsCommentsSectionOpen={setIsCommentsSectionOpen}
                data={headerData}
                pipeline={detailsData?.stage?.pipeline}
                activeView={activeView}
                onChangeActiveView={onChangeActiveView}
                companyTags={combinedTags}
                companyId={companyId}
                stageName={detailsData?.stage?.name || ""}
                triggerRefetch={refetchDetailsData}
                refetchDetailsWithPolledData={refetchDetailsData}
                dataStatusFromAccess={dataStatusFromAccess}
                triggerRefetchAccess={refetchFounderInfo}
                isLoadingRequestAccess={isLoadingRequestAccess}
                setIsLoadingRequestAccess={setIsLoadingRequestAccess}
                isDataRoomPending={founderInfoData?.isDataRoomPending}
                isContainerScrolled={isContainerScrolled}
              />
              <AnnotationsSection id={detailsData?.companyData.id || 0} />
            </div>
          </div>
        );
      case activeView === EActiveView.PROFILE && !hasInnoveraData && !hasPitchdeckData:
        navigate(getRouteDashboard());
        snackbarConstructor("Company has no data yet", "error");
      default:
        return (
          <div className={styles.container}>
            <div className={styles.headerContainer}>
              <div className={styles.headerContent} ref={headerRef}>
                <InnoveraCompanyHeader />
                <HeaderSection
                  pipeline={detailsData?.stage?.pipeline}
                  setIsCommentsSectionOpen={setIsCommentsSectionOpen}
                  data={headerData}
                  activeView={activeView}
                  onChangeActiveView={onChangeActiveView}
                  companyTags={combinedTags}
                  companyId={companyId}
                  stageName={detailsData?.stage?.name || ""}
                  triggerRefetch={refetchDetailsData}
                  refetchDetailsWithPolledData={refetchDetailsData}
                  dataStatusFromAccess={dataStatusFromAccess}
                  triggerRefetchAccess={refetchFounderInfo}
                  isLoadingRequestAccess={isLoadingRequestAccess}
                  setIsLoadingRequestAccess={setIsLoadingRequestAccess}
                  isDataRoomPending={founderInfoData?.isDataRoomPending}
                  isContainerScrolled={isContainerScrolled}
                />
              </div>
              {!isMobile && (
                <div className={styles.commentsSidebarContainer}>
                  <Sidebar
                    selectedSection={selectedSection}
                    sectionRefs={sectionRefs}
                    top={headerHeight}
                    sidebarRef={sidebarRef}
                  />
                  {showComments && (
                    <div className={styles.commentsContainer}>
                      <Comments
                        data={notesData.summarySection}
                        value={noteValue}
                        setValue={setNoteValue}
                        postNote={onClickPost}
                        triggerRefetch={refetchListNotes}
                        deleteFn={(noteId) => deletePortfolioCompanySectionNote({ companyId, noteId })}
                        patchFn={(content, noteId) => patchPortfolioCompanySectionNote({ companyId, noteId, content })}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className={clsx(styles.contentBlock, isMobile && styles.mobile)} style={styleMarginTop}>
              <div className={styles.subContentContainer}>
                <OverviewSection
                  onReportError={onReportError}
                  innoveraData={detailsData?.companyData}
                  logoUrl={logoUrl}
                  aiProvidedDescription={detailsData?.description || ""}
                  className={sectionStyles}
                  innerRef={sectionRefs[ESectionOptions.OVERVIEW]}
                  dataStatusFromAccess={dataStatusFromAccess}
                  onConfirmRequestDataAccess={onConfirmRequestDataAccess}
                  isLoadingRequestAccess={isLoadingRequestAccess}
                  companyName={detailsData?.name ?? detailsData?.companyData?.name}
                  isLoadingAiProvidedDescription={detailsDataStatus !== EDataStatus.done}
                  isLoadingInnoveraData={detailsDataStatus !== EDataStatus.done}
                />
                <FinancingSection
                  onReportError={onReportError}
                  innerRef={sectionRefs[ESectionOptions.FINANCING]}
                  className={sectionStyles}
                  innoveraData={detailsData?.companyData}
                  isLoadingInnoveraData={detailsDataStatus !== EDataStatus.done}
                />
                <TeamSection
                  isLoadingTeamData={isLoadingTeamData}
                  teamData={teamData}
                  onReportError={onReportError}
                  innerRef={sectionRefs[ESectionOptions.TEAM]}
                  className={sectionStyles}
                  aiTeamData={detailsData?.teamMembers}
                  dataStatusFromAccess={dataStatusFromAccess}
                  onConfirmRequestDataAccess={onConfirmRequestDataAccess}
                  isLoadingRequestAccess={isLoadingRequestAccess}
                  isLoadingAiTeamData={detailsDataStatus !== EDataStatus.done}
                  companyName={companyName}
                  logoUrl={logoUrl}
                />
                <MarketOverviewSection
                  onReportError={onReportError}
                  innerRef={sectionRefs[ESectionOptions.MARKET]}
                  className={sectionStyles}
                  dataStatusFromAccess={dataStatusFromAccess}
                  onConfirmRequestDataAccess={onConfirmRequestDataAccess}
                  isLoadingRequestAccess={isLoadingRequestAccess}
                  aiProvidedDescription={detailsData?.marketDescription || ""}
                  isLoadingAiProvidedDescription={detailsDataStatus !== EDataStatus.done}
                  companyName={companyName}
                  logoUrl={logoUrl}
                  isLoadingMarketOverviewData={isLoadingMarketOverviewData}
                  marketOverviewData={marketOverviewData}
                  marketOverviewDataStatus={marketOverviewDataStatus}
                />
                <SolutionOverviewSection
                  onReportError={onReportError}
                  innerRef={sectionRefs[ESectionOptions.PRODUCTS_AND_SERVICES]}
                  className={sectionStyles}
                  aiProvidedDescription={detailsData?.solutionDescription || ""}
                  dataStatusFromAccess={dataStatusFromAccess}
                  onConfirmRequestDataAccess={onConfirmRequestDataAccess}
                  isLoadingRequestAccess={isLoadingRequestAccess}
                  companyName={companyName}
                  logoUrl={logoUrl}
                  isLoadingPatents={isLoadingPatentsData}
                  patentsData={patentsData}
                  isLoadingProducts={isLoadingProductsData}
                  productsData={productsData}
                  isLoadingAiProvidedDescription={detailsDataStatus !== EDataStatus.done}
                  productsDataStatus={productsDataStatus}
                />
                <MarketLandscapeSection
                  onReportError={onReportError}
                  companyName={companyName}
                  logoUrl={logoUrl}
                  innerRef={sectionRefs[ESectionOptions.COMPETITIVE_LANDSCAPE]}
                  className={sectionStyles}
                  dataStatusFromAccess={dataStatusFromAccess}
                  onConfirmRequestDataAccess={onConfirmRequestDataAccess}
                  isLoadingRequestAccess={isLoadingRequestAccess}
                  isInvestor
                  competitors={competitorCompaniesData}
                  isLoadingCompetitors={isLoadingCompetitorCompaniesData || competitorsDataStatus !== EDataStatus.done}
                  refetchCompetitors={refetchCompetitorCompaniesData}
                  isLoadingSimilarCompaniesData={isLoadingSimilarCompaniesData}
                  similarCompaniesData={similarCompaniesData}
                  refetchSimilarCompaniesData={refetchSimilarCompaniesData}
                />
              </div>
            </div>
          </div>
        );
    }
  }, [activeView, selectedSection, isLoadingDetailsData, isFetchingDetailsData, notesData]);

  return (
    <>
      {content}
      <InvestorReportAnErrorModal
        isOpen={isReportErrorModalOpen}
        onClose={() => setIsReportErrorModalOpen(false)}
        companyId={companyId}
      />
      <CommentsDrawer
        isOpen={isCommentsSectionOpen && !showComments}
        onClose={() => setIsCommentsSectionOpen(false)}
        position="bottom"
        data={notesData.summarySection}
        value={noteValue}
        setValue={setNoteValue}
        postNote={onClickPost}
        triggerRefetch={refetchListNotes}
        deleteFn={(noteId) => deletePortfolioCompanySectionNote({ companyId, noteId })}
        patchFn={(content, noteId) => patchPortfolioCompanySectionNote({ companyId, noteId, content })}
      />
    </>
  );
}
